import React, { useEffect, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const FALLBACK_NEWS_IMAGE = "/img/news_placeholder.png";
const IMAGE_RETRY_DELAY_MS = 300;

function isDataImageUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

async function fetchNewsImageWithRetry(noticiaId) {
  const fetchOnce = async () => {
    const r = await fetch(`${API_BASE}/noticias/${noticiaId}/imagen`, { credentials: "omit" });
    if (!r.ok) throw new Error("news_image_fetch_failed");
    const txt = await r.text();
    if (!isDataImageUrl(txt)) throw new Error("invalid_image_payload");
    return txt;
  };

  try {
    return await fetchOnce();
  } catch {
    try {
      await new Promise((resolve) => setTimeout(resolve, IMAGE_RETRY_DELAY_MS));
      return await fetchOnce();
    } catch {
      return FALLBACK_NEWS_IMAGE;
    }
  }
}

export default function NoticiasSidebar({ visible, setVisible, offsetTop = 75, buttonRef }) {
  const [noticias, setNoticias] = useState([]);
  const [imgs, setImgs] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [seleccionada, setSeleccionada] = useState(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const outside = (e) => {
      const clickEnSidebar = sidebarRef.current && sidebarRef.current.contains(e.target);

      const clickEnBoton = buttonRef.current && buttonRef.current.contains(e.target);

      if (!clickEnSidebar && !clickEnBoton) {
        setVisible(false);
      }
      // if (sidebarRef.current && !sidebarRef.current.contains(e.target)) setVisible(false);
    };
    if (visible) document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [visible]);

  useEffect(() => {
    const fetchNoticias = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await fetch(`${API_BASE}/noticias/vigentes`, {
          method: "GET",
          credentials: "omit",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          setErrorMsg(`No se pudo acceder a las noticias (${res.status}).`);
          setNoticias([]);
          return;
        }
        const data = await res.json();
        const ordenadas = (Array.isArray(data) ? data : [])
          .sort((a, b) => new Date(b.fechaPublicacion) - new Date(a.fechaPublicacion))
          .slice(0, 10);

        setNoticias(ordenadas);

        const pairs = await Promise.all(
          ordenadas.map(async (n) => {
            const src = await fetchNewsImageWithRetry(n.id);
            return [n.id, src];
          })
        );
        setImgs(Object.fromEntries(pairs));
      } catch (e) {
        setErrorMsg("Error de red al cargar noticias.");
        setNoticias([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
  }, []);

  // ---------- Modal de noticia ----------
  function NoticiaModal({ noticia, onClose, imagen }) {
    useEffect(() => {
      const onKey = (e) => e.key === "Escape" && onClose();
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", onKey);
      };
    }, [onClose]);

    if (!noticia) return null;

    const fecha = new Date(noticia.fechaPublicacion).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    return (
      <div
        className="fixed inset-0 z-[2000] bg-black/60 backdrop-blur-[1px] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="noticia-title"
        onClick={onClose}
      >
        <div
          className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <h3 id="noticia-title" className="text-lg font-semibold text-gray-800 line-clamp-2">
              {noticia.titulo}
            </h3>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border hover:bg-gray-100"
              aria-label="Cerrar"
              title="Cerrar"
            >
              ×
            </button>
          </div>

          {/* Imagen */}
          {imagen ? (
            <img
              src={imagen}
              alt={noticia.titulo}
              className="w-full max-h-[46vh] object-cover bg-gray-50"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_NEWS_IMAGE;
              }}
            />
          ) : null}

          {/* Cuerpo */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
              <span>Publicado: {fecha}</span>
              {noticia.fechaExpiracion && (
                <span>Vigente hasta: {new Date(noticia.fechaExpiracion).toLocaleDateString("es-ES")}</span>
              )}
            </div>
            <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap">
              {noticia.contenido}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <aside
        ref={sidebarRef}
        className={`fixed right-0 bg-white shadow-xl border-l border-gray-200 z-[1000] w-80 transition-transform duration-500 ease-in-out ${visible ? "translate-x-0" : "translate-x-full"
          }`}
        style={{ top: offsetTop, height: `calc(100vh - ${offsetTop}px)` }}
      >
        <div className="p-4 overflow-y-auto h-full">
          <h2 className="text-xl font-bold mb-2 text-gray-800 border-b pb-2">Noticias</h2>

          {loading && <p className="text-sm text-gray-500">Cargando noticias...</p>}
          {!loading && errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
          {!loading && !errorMsg && noticias.length === 0 && (
            <p className="text-sm text-gray-500">No hay noticias publicadas actualmente.</p>
          )}

          {!loading &&
            !errorMsg &&
            noticias.map((n) => {
              const fecha = new Date(n.fechaPublicacion).toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
              const imgSrc = imgs[n.id] || FALLBACK_NEWS_IMAGE;
              return (
                <div
                  key={n.id}
                  className="bg-white border rounded-xl shadow-sm hover:shadow-md transition overflow-hidden flex flex-col mb-5"
                >
                  <img
                    src={imgSrc}
                    alt={n.titulo}
                    className="w-full h-36 object-cover"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_NEWS_IMAGE;
                    }}
                  />
                  <div className="p-3 flex flex-col flex-1">
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 mb-1">{n.titulo}</h3>
                    <p className="text-xs text-gray-600 flex-1 line-clamp-3">
                      {(n.contenido || "").slice(0, 130)}
                      {(n.contenido || "").length > 130 ? "..." : ""}
                    </p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-[11px] text-gray-400">{fecha}</span>
                      <button
                        onClick={() => setSeleccionada(n)}
                        className="text-[12px] text-red-600 font-medium hover:underline"
                      >
                        Leer más →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </aside>

      {/* Modal */}
      {seleccionada && (
        <NoticiaModal
          noticia={seleccionada}
          imagen={imgs[seleccionada.id]}
          onClose={() => setSeleccionada(null)}
        />
      )}
    </>
  );
}
